import React, { useState } from "react";
import {
  Box,
  Button,
  Link,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { EyeCloseIcon, EyeOpenIcon } from "../../../@lib/icons";
import { useLanguageTranslator } from "../../../components";
import { gql, useMutation } from "@apollo/client";
import { useAuthSession } from "../../../components/AuthSessionProvider";
import { toastHandler } from "../../../utils/helpers";
import { toastTypesKeys } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

const resetPasswordQuery = gql`
  mutation ResetPassword(
    $passwordToken: String!
    $password: String!
    $passwordConfirmation: String!
  ) {
    resetPassword(
      input: {
        passwordToken: $passwordToken
        password: $password
        passwordConfirmation: $passwordConfirmation
      }
    ) {
      clientMutationId
      message
    }
  }
`;

function NewPassword() {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [filledBars, setFilledBars] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { translator } = useLanguageTranslator();
  const navigate = useNavigate();
  const { passwordToken, resetPasswordToken } = useAuthSession() as any;

  const [resetPassword, { data, loading, error }] =
    useMutation(resetPasswordQuery);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      // password strength logic (simple: based on length)
      if (value.length >= 8) setFilledBars(4);
      else if (value.length >= 6) setFilledBars(3);
      else if (value.length >= 4) setFilledBars(2);
      else if (value.length > 0) setFilledBars(1);
      else setFilledBars(0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Submitted:", formData);

    try {
      const response = await resetPassword({
        variables: {
          passwordToken: passwordToken, // replace with actual token
          password: formData?.password,
          passwordConfirmation: formData?.confirmPassword,
        },
      });
      toastHandler(
        response?.data?.resetPassword?.message || "",
        toastTypesKeys.success
      );
      setTimeout(async () => {
        await resetPasswordToken("");
        navigate(`/auth/login`);
      }, 2000);
      return;
    } catch (err) {
      console.error("Error during mutation:", err);
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  };

  const isFormValid =
    formData.password.length >= 8 &&
    formData.password === formData.confirmPassword;

  return (
    <Stack sx={{ height: "100%" }} justifyContent="center">
      <ToastContainer />
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ width: "100%", maxWidth: 400, mx: "auto" }}
      >
        <Stack spacing={3} alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {translator(`Setup New Password`)}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {translator(`Have you already reset the password`)}?{" "}
            <Link
              href="/auth/login"
              underline="hover"
              fontWeight="bold"
              color="primary"
            >
              {translator(`Sign in`)}
            </Link>
          </Typography>
        </Stack>

        {/* Password input */}
        <Stack spacing={1} mb={2}>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label={translator(`generic.Password`)}
            name="password"
            value={formData.password}
            onChange={handleChange}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <EyeOpenIcon /> : <EyeCloseIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Password strength meter */}
          <LinearProgress
            variant="determinate"
            value={(filledBars / 4) * 100}
            sx={{
              height: 6,
              borderRadius: 5,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {translator(`Use 8 or more characters`)}.
          </Typography>
        </Stack>

        {/* Confirm password */}
        <TextField
          fullWidth
          type={showConfirm ? "text" : "password"}
          label={translator(`Repeat Password`)}
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          variant="outlined"
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirm((prev) => !prev)}
                  edge="end"
                >
                  {showConfirm ? <EyeOpenIcon /> : <EyeCloseIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Submit button */}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={!isFormValid}
        >
          {translator(`Submit`)}
        </Button>
      </Box>
    </Stack>
  );
}

export { NewPassword };
